import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Download as DownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import TableCommon from "../../Common/Table";
import { SearchRecords } from "../../Common/SearchRecords";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useCreateWeeklyPayoutBatchMutation,
    useFailWithdrawalMutation,
    useGetPayoutBatchesQuery,
    useGetPayoutBatchQuery,
    useGetWithdrawalsQuery,
    useMarkWithdrawalPaidMutation,
    useRejectWithdrawalMutation,
    useResolveLegacyWithdrawalMutation,
} from "../../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { rupees } from "./affiliateUtils";

const STATUS = {
    requested: { label: "Waiting for weekly batch", color: "warning" },
    processing: { label: "In weekly batch", color: "info" },
    paid: { label: "Paid", color: "success" },
    failed: { label: "Transfer failed, refunded", color: "error" },
    rejected: { label: "Rejected, refunded", color: "default" },
};

const columns = [
    { title: "Requested", field: "created_at", sort: false, width: "13%" },
    { title: "Customer", field: "customer", sort: false, width: "18%" },
    { title: "Amount", field: "amount", sort: false, width: "10%", align: "right" },
    { title: "Pay to", field: "destination", sort: false, width: "24%" },
    { title: "Status", field: "status", sort: false, width: "15%", align: "center" },
    { title: "Actions", field: "actions", sort: false, width: "20%", align: "right" },
];

const RejectDialog = ({ withdrawal, onClose }) => {
    const dispatch = useDispatch();
    const [reason, setReason] = useState("");
    const [reject, { isLoading }] = useRejectWithdrawalMutation();
    const submit = async () => {
        if (reason.trim().length < 3) return;
        try {
            await reject({ id: withdrawal.id, reason: reason.trim() }).unwrap();
            dispatch(setSnackBar({ open: true, message: "Rejected. Funds returned to the wallet.", severity: "success" }));
            onClose();
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not reject", severity: "error" }));
        }
    };
    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Reject {rupees(withdrawal.amount)} request</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The reserved amount returns to {withdrawal.customer?.name}'s wallet. They will see this reason.
                </Typography>
                <TextField autoFocus fullWidth label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 255 }} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button fullWidth={false} variant="text" onClick={onClose}>Cancel</Button>
                <Button fullWidth={false} variant="contained" color="error" onClick={submit} loading={isLoading} disabled={reason.trim().length < 3}>Reject</Button>
            </DialogActions>
        </Dialog>
    );
};

const TransferDialog = ({ batchId, withdrawal, failed, onClose }) => {
    const dispatch = useDispatch();
    const [value, setValue] = useState("");
    const [markPaid, { isLoading: paying }] = useMarkWithdrawalPaidMutation();
    const [markFailed, { isLoading: failing }] = useFailWithdrawalMutation();
    const submit = async () => {
        if (value.trim().length < (failed ? 3 : 6)) return;
        try {
            if (failed) {
                await markFailed({ batchId, withdrawalId: withdrawal.id, reason: value.trim() }).unwrap();
                dispatch(setSnackBar({ open: true, message: "Marked failed. Funds returned to the wallet.", severity: "success" }));
            } else {
                await markPaid({ batchId, withdrawalId: withdrawal.id, utr: value.trim() }).unwrap();
                dispatch(setSnackBar({ open: true, message: "Manual transfer recorded as paid.", severity: "success" }));
            }
            onClose();
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not update the transfer", severity: "error" }));
        }
    };
    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{failed ? "Record failed transfer" : `Record ${rupees(withdrawal.amount)} as paid`}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {failed
                        ? "The request amount will return to the customer's wallet."
                        : "Enter the bank UTR or transfer reference after confirming the transfer in your bank."}
                </Typography>
                <TextField autoFocus fullWidth label={failed ? "Failure reason" : "Bank UTR / reference"} value={value} onChange={(e) => setValue(e.target.value)} inputProps={{ maxLength: failed ? 255 : 64 }} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button fullWidth={false} variant="text" onClick={onClose}>Cancel</Button>
                <Button fullWidth={false} variant="contained" color={failed ? "error" : "primary"} onClick={submit} loading={paying || failing} disabled={value.trim().length < (failed ? 3 : 6)}>
                    {failed ? "Return funds" : "Mark paid"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const LegacyResolutionDialog = ({ withdrawal, onClose }) => {
    const dispatch = useDispatch();
    const [outcome, setOutcome] = useState("paid");
    const [value, setValue] = useState("");
    const [resolve, { isLoading }] = useResolveLegacyWithdrawalMutation();
    const submit = async () => {
        if (value.trim().length < (outcome === "paid" ? 6 : 3)) return;
        try {
            const body = outcome === "paid" ? { id: withdrawal.id, outcome, utr: value.trim() } : { id: withdrawal.id, outcome, reason: value.trim() };
            await resolve(body).unwrap();
            dispatch(setSnackBar({ open: true, message: outcome === "paid" ? "Earlier payout recorded as paid." : "Earlier payout marked failed; funds returned.", severity: "success" }));
            onClose();
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not reconcile the earlier payout", severity: "error" }));
        }
    };
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Reconcile earlier payout</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This withdrawal was started by the previous Cashfree payout flow. Check its provider record before choosing an outcome. Do not send another transfer unless Cashfree confirms the earlier payout failed.
                    {withdrawal.legacy_payout?.reference ? ` Reference: ${withdrawal.legacy_payout.reference}.` : " No provider reference is recorded."}
                    {withdrawal.legacy_payout?.provider_status ? ` Provider status: ${withdrawal.legacy_payout.provider_status}.` : ""}
                </Alert>
                <TextField select fullWidth label="Verified outcome" value={outcome} onChange={(e) => { setOutcome(e.target.value); setValue(""); }} sx={{ mb: 2 }}>
                    <MenuItem value="paid">Cashfree confirms paid</MenuItem>
                    <MenuItem value="failed">Cashfree confirms failed</MenuItem>
                </TextField>
                <TextField autoFocus fullWidth label={outcome === "paid" ? "Confirmed UTR / transfer reference" : "Failure reason"} value={value} onChange={(e) => setValue(e.target.value)} inputProps={{ maxLength: outcome === "paid" ? 64 : 255 }} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button fullWidth={false} variant="text" onClick={onClose}>Cancel</Button>
                <Button fullWidth={false} variant="contained" color={outcome === "failed" ? "error" : "primary"} onClick={submit} loading={isLoading} disabled={value.trim().length < (outcome === "paid" ? 6 : 3)}>
                    {outcome === "paid" ? "Record paid" : "Return funds"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const csvCell = (value) => {
    const text = String(value ?? "");
    const safe = /^[=+@\-\t\r]/.test(text) ? `'${text}` : text;
    return `"${safe.replaceAll('"', '""')}"`;
};

const downloadBatchCsv = (batch) => {
    const rows = (batch?.withdrawals || []).filter((w) => w.status === "processing");
    if (!rows.length) return;
    const header = ["Batch", "Withdrawal ID", "Customer", "Mobile", "Amount INR", "Method", "Account holder", "UPI ID", "Bank account number", "IFSC"];
    const body = rows.map((w) => [
        batch.batch_number,
        w.id,
        w.customer?.name,
        w.customer?.mobile,
        Number(w.amount).toFixed(2),
        w.payment_details?.method,
        w.payment_details?.account_holder,
        w.payment_details?.vpa,
        w.payment_details?.bank_account_number,
        w.payment_details?.bank_ifsc,
    ]);
    const csv = [header, ...body].map((line) => line.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${batch.batch_number}-manual-transfers.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const WithdrawalsAdmin = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({ pageNo: 0, limit: 10, sortBy: "DESC", sortField: "created_at", keywords: "" });
    const [status, setStatus] = useState("requested");
    const [rejecting, setRejecting] = useState(null);
    const [transferAction, setTransferAction] = useState(null);
    const [legacyWithdrawal, setLegacyWithdrawal] = useState(null);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const { data, isFetching, error } = useGetWithdrawalsQuery({
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        keywords: pagination.keywords,
        status,
    });
    const { data: batches = [], isFetching: batchesLoading } = useGetPayoutBatchesQuery();
    const { data: selectedBatch, isFetching: batchLoading } = useGetPayoutBatchQuery(selectedBatchId, { skip: !selectedBatchId });
    const [createBatch, { isLoading: creatingBatch }] = useCreateWeeklyPayoutBatchMutation();

    const doCreateBatch = async () => {
        if (!data?.count) return;
        const confirmed = window.confirm(
            `Create a weekly batch with all ${data.count} waiting requests for ${rupees(data.requested_amount)}? Requests will be reserved in this batch for manual bank transfer.`,
        );
        if (!confirmed) return;
        try {
            const batch = await createBatch().unwrap();
            setSelectedBatchId(batch?.id || null);
            setStatus("processing");
            setPagination((p) => ({ ...p, pageNo: 0 }));
            dispatch(setSnackBar({ open: true, message: `${batch?.batch_number} created. Download the transfer CSV and pay from the company bank account.`, severity: "success" }));
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not create the weekly batch", severity: "error" }));
        }
    };

    const rows = useMemo(
        () =>
            !isFetching && data?.results?.length
                ? data.results.map((w) => (
                      <TableRow key={w.id} hover sx={{ "td, th": { border: 0, padding: "14px 12px" } }}>
                          <TableCell>{handleDateTimeDefault(w.created_at)}</TableCell>
                          <TableCell>
                              <Typography variant="body2" fontWeight={600}>{w.customer?.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{w.customer?.mobile}</Typography>
                          </TableCell>
                          <TableCell align="right"><strong>{rupees(w.amount)}</strong></TableCell>
                          <TableCell>
                              <Typography variant="body2">{w.account_holder}</Typography>
                              <Typography variant="caption" color="text.secondary">{w.method === "upi" ? "UPI " : "Bank "}{w.destination}</Typography>
                              {w.payout_batch_number ? <Typography variant="caption" display="block">Batch {w.payout_batch_number}</Typography> : null}
                              {w.utr ? <Typography variant="caption" display="block" color="success.main">UTR {w.utr}</Typography> : null}
                              {w.failure_reason ? <Typography variant="caption" display="block" color="error.main">{w.failure_reason}</Typography> : null}
                          </TableCell>
                          <TableCell align="center"><Chip size="small" {...(STATUS[w.status] || { label: w.status })} /></TableCell>
                          <TableCell align="right">
                              {w.status === "requested" ? (
                                  <Button fullWidth={false} size="small" variant="outlined" color="error" onClick={() => setRejecting(w)}>Reject</Button>
                              ) : null}
                              {w.status === "processing" && w.payout_batch_id ? (
                                  <Button fullWidth={false} size="small" variant="outlined" onClick={() => { setSelectedBatchId(w.payout_batch_id); setTransferAction({ withdrawal: w, failed: false }); }}>Record transfer</Button>
                              ) : null}
                              {w.status === "processing" && !w.payout_batch_id ? (
                                  <Button fullWidth={false} size="small" variant="outlined" color="warning" onClick={() => setLegacyWithdrawal(w)}>Reconcile old payout</Button>
                              ) : null}
                          </TableCell>
                      </TableRow>
                  ))
                : null,
        [data, isFetching],
    );

    const batchRows = batches.map((batch) => (
        <TableRow key={batch.id} hover selected={batch.id === selectedBatchId}>
            <TableCell>{batch.batch_number}</TableCell>
            <TableCell>{handleDateTimeDefault(batch.created_at)}</TableCell>
            <TableCell align="right">{batch.paid_count}/{batch.item_count} paid{batch.failed_count ? ` · ${batch.failed_count} failed` : ""}</TableCell>
            <TableCell align="right">{rupees(batch.paid_amount || 0)} / {rupees(batch.total_amount)}</TableCell>
            <TableCell><Chip size="small" color={batch.status === "completed" ? "success" : "info"} label={batch.status === "completed" ? "Completed" : "Awaiting manual transfers"} /></TableCell>
            <TableCell align="right"><Button fullWidth={false} size="small" variant="text" startIcon={<VisibilityIcon />} onClick={() => setSelectedBatchId(batch.id)}>View</Button></TableCell>
        </TableRow>
    ));

    const selectedRows = selectedBatch?.withdrawals || [];

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    Weekly referral withdrawals
                    {data?.requested_amount ? <Chip size="small" color="warning" label={`${rupees(data.requested_amount)} waiting`} sx={{ ml: 1 }} /> : null}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPagination((p) => ({ ...p, pageNo: 0 })); }} sx={{ minWidth: 190 }}>
                        <MenuItem value="all">All</MenuItem>
                        {Object.entries(STATUS).map(([key, meta]) => <MenuItem key={key} value={key}>{meta.label}</MenuItem>)}
                    </TextField>
                    <Box sx={{ minWidth: 220 }}><SearchRecords handlePagination={(p) => setPagination(p)} pagination={pagination} /></Box>
                </Stack>
            </Stack>
            <Alert severity="info">
                Requests reserve the customer's wallet balance. Create one weekly batch, use its CSV as a transfer checklist, make transfers from the company bank account, then record each bank reference. The CSV is not a bank-specific upload file. No money is sent automatically.
            </Alert>
            {status === "requested" && !pagination.keywords && data?.count ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Button fullWidth={false} variant="contained" onClick={doCreateBatch} loading={creatingBatch}>
                        Create weekly batch ({data.count} · {rupees(data.requested_amount)})
                    </Button>
                    <Typography variant="caption" color="text.secondary">Includes all requests waiting for this week's payment cycle.</Typography>
                </Stack>
            ) : null}
            {error ? <Alert severity="error">{error?.data?.message || "Failed to load withdrawals"}</Alert> : null}
            <TableCommon columns={columns} tableData={rows} count={data?.count || 0} loading={isFetching} pagination={pagination} handlePagination={(p) => setPagination(p)} emptyMessage={status === "requested" ? "No withdrawal requests waiting for a weekly batch" : "No withdrawals"} />

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Recent weekly batches</Typography>
                <Table size="small">
                    <TableHead><TableRow><TableCell>Batch</TableCell><TableCell>Created</TableCell><TableCell align="right">Requests</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell align="right">Details</TableCell></TableRow></TableHead>
                    <TableBody>
                        {batchRows}
                        {!batchesLoading && !batches.length ? <TableRow><TableCell colSpan={6}><Typography color="text.secondary" variant="body2">No weekly batches yet.</Typography></TableCell></TableRow> : null}
                    </TableBody>
                </Table>
            </Paper>

            {selectedBatchId ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1} sx={{ mb: 1 }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{selectedBatch?.batch_number || "Payout batch"}</Typography>
                            {selectedBatch ? <Typography variant="body2" color="text.secondary">{selectedBatch.item_count} requests · {rupees(selectedBatch.total_amount)} · {selectedBatch.status}</Typography> : null}
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button fullWidth={false} variant="outlined" startIcon={<DownloadIcon />} disabled={!selectedRows.some((w) => w.status === "processing")} onClick={() => downloadBatchCsv(selectedBatch)}>Download unpaid transfers CSV</Button>
                            <Button fullWidth={false} variant="text" onClick={() => setSelectedBatchId(null)}>Close</Button>
                        </Stack>
                    </Stack>
                    {batchLoading ? <Typography color="text.secondary">Loading batch…</Typography> : null}
                    {!batchLoading && selectedBatch ? (
                        <Table size="small">
                            <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Amount</TableCell><TableCell>Transfer destination</TableCell><TableCell>Status / reference</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                            <TableBody>
                                {selectedRows.map((w) => (
                                    <TableRow key={w.id}>
                                        <TableCell><Typography variant="body2" fontWeight={600}>{w.customer?.name}</Typography><Typography variant="caption">{w.customer?.mobile}</Typography></TableCell>
                                        <TableCell>{rupees(w.amount)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{w.payment_details?.account_holder}</Typography>
                                            {w.method === "upi" ? <Typography variant="caption">UPI {w.payment_details?.vpa}</Typography> : <Typography variant="caption">A/C {w.payment_details?.bank_account_number} · {w.payment_details?.bank_ifsc}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" {...(STATUS[w.status] || { label: w.status })} />
                                            {w.utr ? <Typography variant="caption" display="block">UTR {w.utr}</Typography> : null}
                                            {w.failure_reason ? <Typography variant="caption" color="error.main" display="block">{w.failure_reason}</Typography> : null}
                                        </TableCell>
                                        <TableCell align="right">
                                            {w.status === "processing" ? <Stack direction="row" spacing={1} justifyContent="flex-end"><Button fullWidth={false} size="small" variant="contained" onClick={() => setTransferAction({ withdrawal: w, failed: false })}>Mark paid</Button><Button fullWidth={false} size="small" variant="outlined" color="error" onClick={() => setTransferAction({ withdrawal: w, failed: true })}>Failed</Button></Stack> : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!selectedRows.length ? <TableRow><TableCell colSpan={5}>No requests in this batch.</TableCell></TableRow> : null}
                            </TableBody>
                        </Table>
                    ) : null}
                </Paper>
            ) : null}

            {rejecting ? <RejectDialog withdrawal={rejecting} onClose={() => setRejecting(null)} /> : null}
            {transferAction ? <TransferDialog batchId={selectedBatchId} withdrawal={transferAction.withdrawal} failed={transferAction.failed} onClose={() => setTransferAction(null)} /> : null}
            {legacyWithdrawal ? <LegacyResolutionDialog withdrawal={legacyWithdrawal} onClose={() => setLegacyWithdrawal(null)} /> : null}
        </Stack>
    );
};

export default WithdrawalsAdmin;
